import React, { useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import env from '@maui-mf/env'
import { AUTH_REDIRECT_PATH } from '@maui-mf/common-auth'
import { useAuthProvider, AuthProvider } from '@maui-mf/common-react'
import { normalizeSelectedOrg } from './api/normalizers'
import { userAccessToResource, genHasEntitlements } from './auth/entitlements'

const AuthContext = React.createContext({})

const AdaptedAuthProvider = ({ children }) => {
  const { claims, userinfo: userInfo, token } = useAuthProvider()

  const value = useMemo(() => {
    if (!claims) {
      return
    }

    const hasEntitlements = genHasEntitlements(claims)
    const normalizedSelectedOrg = normalizeSelectedOrg(claims)

    return {
      hasAccessToObject: object => userAccessToResource(object, hasEntitlements),
      hasFusionAccess: hasEntitlements(['INTEL_RBI_FUS']),
      hasVulnAccess: hasEntitlements(['INTEL_RBI_VULN']),
      isFreemium: !hasEntitlements(['INTEL_RBI_FUS', 'INTEL_RBI_OPS', 'INTEL_RBI_VULN']),
      organizations: claims?.organizations,
      selectedOrg: normalizedSelectedOrg,
      token,
      userInfo,
    }
  }, [claims, token, userInfo])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

AdaptedAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export function AuthContextProvider({ children }) {
  const redirectUri = new URL(AUTH_REDIRECT_PATH, env.MAUI_HOST || env?.default?.MAUI_HOST)
  return (
    <AuthProvider
      clientId={env.MAUI_IDP_CLIENT_ID || env?.default?.MAUI_IDP_CLIENT_ID}
      host={env.MAUI_IDP_HOST || env?.default?.MAUI_IDP_HOST}
      redirectUri={redirectUri.href}
    >
      <AdaptedAuthProvider>
        {children}
      </AdaptedAuthProvider>
    </AuthProvider>
  )
}

export function useAuthContext() {
  return useContext(AuthContext)
}

AuthContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default AuthContext
